import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useCallback, useState, MouseEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from './i18n';
import api from '../../config/axios';

interface BargeSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (barge: Barge) => void;
}

interface Barge {
  systemId: string;
  name: string;
}

interface BargeResponse {
  barges: Barge[];
}

export const BargeSelectModal = ({ open, onClose, onSelect }: BargeSelectModalProps) => {
  const { t } = useTranslation(Settings);
  const [searchQuery, setSearchQuery] = useState('');
  const [barges, setBarges] = useState<Barge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBarges = async () => {
    try {
      setLoading(true);
      const { data } = await api.get<BargeResponse>('/data-logger/mfm/settings/barge');
      setBarges(data.barges.map((barge: any) => ({ 
        systemId: barge.systemId,
        name: barge.name
      })));
      setError(null);
    } catch (err) {
      console.error('Error fetching barges:', err);
      setError('Failed to fetch barges list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBarges();
    }
  }, [open]);

  const filteredBarges = barges.filter((barge) =>
    barge.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOnClose = useCallback(
    (event: MouseEvent, reason: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
        },
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2 }}
          fontFamily="Noto Sans"
          fontWeight={500}
          fontSize="16px"
          lineHeight="28px"
          letterSpacing="0px"
          display="flex"
          alignItems="center"
        >
          {t('generalSettings.selectBarge')}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('generalSettings.searchBarge')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F8F8F8',
              },
            }}
          />
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : (
          <List sx={{ pt: 0 }}>
            {filteredBarges.map((barge) => (
              <ListItem
                key={barge.systemId}
                onClick={() => {
                  onSelect(barge);
                  onClose();
                }}
                sx={{
                  borderRadius: 1,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.selected,
                  },
                }}
              >
                <ListItemText primary={barge.name} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
